import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure pdfjs worker to use unpkg ESM worker matching installed version
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
  url: string;
  onLoadSuccess?: () => void;
  onLoadError?: () => void;
}

export function PdfViewer({ url, onLoadSuccess, onLoadError }: PdfViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<number>(1);

  useEffect(() => {
    let isCancelled = false;

    const renderPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument({
          url,
          withCredentials: true,
        });

        const pdf = await loadingTask.promise;
        if (isCancelled) return;

        const container = containerRef.current;
        if (!container) return;
        container.innerHTML = ''; // clear previous canvases

        // Determine target rendering width based on container width
        const availableWidth = container.clientWidth || Math.min(window.innerWidth - 24, 800);

        for (let i = 1; i <= pdf.numPages; i++) {
          if (isCancelled) break;

          const page = await pdf.getPage(i);
          const unscaledViewport = page.getViewport({ scale: 1 });
          const targetScale = Math.max(availableWidth / unscaledViewport.width, 0.6) * scale;
          const viewport = page.getViewport({ scale: targetScale });

          const pageWrapper = document.createElement('div');
          pageWrapper.className = 'pdf-canvas-wrapper';
          pageWrapper.style.marginBottom = '12px';
          pageWrapper.style.display = 'flex';
          pageWrapper.style.justifyContent = 'center';
          pageWrapper.style.width = '100%';

          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (!context) continue;

          // Support retina/high-DPI mobile screens
          const outputScale = window.devicePixelRatio || 1;
          canvas.width = Math.floor(viewport.width * outputScale);
          canvas.height = Math.floor(viewport.height * outputScale);
          canvas.style.width = `${Math.floor(viewport.width)}px`;
          canvas.style.height = `${Math.floor(viewport.height)}px`;
          canvas.style.borderRadius = '4px';
          canvas.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.12)';
          canvas.style.background = '#ffffff';

          const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined;

          pageWrapper.appendChild(canvas);
          container.appendChild(pageWrapper);

          await page.render({
            canvasContext: context,
            viewport,
            transform,
          }).promise;
        }

        if (!isCancelled && onLoadSuccess) {
          onLoadSuccess();
        }
      } catch (err) {
        console.error('Failed to render PDF using PDF.js:', err);
        if (!isCancelled && onLoadError) {
          onLoadError();
        }
      }
    };

    renderPdf();

    return () => {
      isCancelled = true;
    };
  }, [url, scale]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <div 
        ref={containerRef} 
        className="pdf-viewer-scroll-container"
        style={{
          width: '100%',
          height: '100%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '70px 6px 12px 6px', // Extra top padding for floating controls
          boxSizing: 'border-box',
          background: '#f1f5f9'
        }}
      />
      
      {/* Floating Zoom Controls */}
      <div style={{
        position: 'absolute',
        top: '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(4px)',
        padding: '6px 16px',
        borderRadius: '20px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 10,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}>
        <button 
          onClick={() => setScale(s => Math.max(s - 0.25, 0.75))}
          disabled={scale <= 0.75}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#ffffff',
            padding: '4px 8px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            opacity: scale <= 0.75 ? 0.4 : 1
          }}
        >
          A-
        </button>
        <span style={{ color: '#ffffff', fontSize: '0.8rem', minWidth: '40px', textAlign: 'center', fontFamily: 'monospace' }}>
          {Math.round(scale * 100)}%
        </span>
        <button 
          onClick={() => setScale(s => Math.min(s + 0.25, 2.5))}
          disabled={scale >= 2.5}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#ffffff',
            padding: '4px 8px',
            fontSize: '1rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            opacity: scale >= 2.5 ? 0.4 : 1
          }}
        >
          A+
        </button>
      </div>
    </div>
  );
}
